/**
 * Dispute — Phase 1c
 * ──────────────────
 * Append-only event model for executor disputes against callbacks
 * and/or holdback entries. Like the holdback ledger, dispute STATE
 * is derived by folding events; there is no mutable status field on
 * the dispute record itself.
 *
 * Hard rules:
 *   • Append-only events. `withdraw` and `resolve` are new events.
 *   • Filing a dispute does NOT auto-pause a holdback. The pause is a
 *     separate, explicit `pauseHoldbackForDispute` ledger entry — the
 *     UI confirms the link in soft mode; hard mode auto-chains both.
 *   • Forbidden keys sanitized.
 */

import { getEnforcementBehavior, getNetworkEnforcementMode } from "../enforcementMode";

export type DisputeEventType = "open" | "evidence_added" | "withdraw" | "resolve";
export type DisputeOutcome = "upheld" | "overturned" | "partial" | "withdrawn";

export interface DisputeEvent {
  id: string;
  disputeId: string;
  type: DisputeEventType;
  /** Subject of dispute. At least one of callbackId or holdbackId must be set on the open event. */
  callbackId?: string;
  holdbackId?: string;
  jobId: string;
  contractorId: string;
  actorId: string;
  actorRole: string;
  reason: string;
  createdAt: string;
  outcome?: DisputeOutcome;
  enforcementModeAtWrite: "soft" | "hard";
}

const FORBIDDEN_DISPUTE_KEYS = [
  "contractValue",
  "customerEmail",
  "customerPhone",
  "ssn",
] as const;

function sanitize<T extends Record<string, unknown>>(payload: T): T {
  const out = { ...payload };
  for (const key of FORBIDDEN_DISPUTE_KEYS) {
    if (key in out) delete (out as Record<string, unknown>)[key];
  }
  return out;
}

function freshId(prefix: string, nowMs: number): string {
  return `${prefix}-${nowMs}-${Math.random().toString(36).slice(2, 8)}`;
}

export interface OpenDisputeInput {
  events: DisputeEvent[];
  jobId: string;
  contractorId: string;
  callbackId?: string;
  holdbackId?: string;
  actorId: string;
  actorRole: string;
  reason: string;
  nowIso?: string;
  id?: string;
  disputeId?: string;
}

export interface AddEvidenceInput {
  events: DisputeEvent[];
  disputeId: string;
  jobId: string;
  contractorId: string;
  actorId: string;
  actorRole: string;
  reason: string;
  nowIso?: string;
  id?: string;
}

export interface WithdrawDisputeInput extends AddEvidenceInput {}

export interface ResolveDisputeInput extends AddEvidenceInput {
  outcome: Exclude<DisputeOutcome, "withdrawn">;
}

export interface DisputeWriteResult {
  events: DisputeEvent[];
  event: DisputeEvent | null;
  error?: string;
}

function validateBase(input: { actorId: string; actorRole: string; reason: string }): string | null {
  if (!input.actorId) return "missing_actor_id";
  if (!input.actorRole) return "missing_actor_role";
  if (!input.reason || input.reason.trim().length < 10) return "reason_too_short";
  return null;
}

export function openDispute(input: OpenDisputeInput): DisputeWriteResult {
  const err = validateBase(input);
  if (err) return { events: input.events, event: null, error: err };
  if (!input.callbackId && !input.holdbackId) {
    return { events: input.events, event: null, error: "no_subject" };
  }
  const now = input.nowIso ?? new Date().toISOString();
  const nowMs = Date.parse(now);
  const disputeId = input.disputeId ?? freshId("dsp", nowMs);
  const sanitizedReason = sanitize({ reason: input.reason }).reason ?? "";
  const event: DisputeEvent = {
    id: input.id ?? freshId("dsp-evt", nowMs),
    disputeId,
    type: "open",
    callbackId: input.callbackId,
    holdbackId: input.holdbackId,
    jobId: input.jobId,
    contractorId: input.contractorId,
    actorId: input.actorId,
    actorRole: input.actorRole,
    reason: sanitizedReason.trim(),
    createdAt: now,
    enforcementModeAtWrite: getNetworkEnforcementMode(),
  };
  return { events: [...input.events, event], event };
}

function appendNonOpen(
  input: AddEvidenceInput,
  type: Exclude<DisputeEventType, "open">,
  outcome?: DisputeOutcome,
): DisputeWriteResult {
  const err = validateBase(input);
  if (err) return { events: input.events, event: null, error: err };
  const exists = input.events.some((e) => e.disputeId === input.disputeId && e.type === "open");
  if (!exists) return { events: input.events, event: null, error: "dispute_not_found" };
  const terminal = input.events.some(
    (e) => e.disputeId === input.disputeId && (e.type === "withdraw" || e.type === "resolve"),
  );
  if (terminal) return { events: input.events, event: null, error: "dispute_already_terminal" };
  const now = input.nowIso ?? new Date().toISOString();
  const sanitizedReason = sanitize({ reason: input.reason }).reason ?? "";
  const event: DisputeEvent = {
    id: input.id ?? freshId("dsp-evt", Date.parse(now)),
    disputeId: input.disputeId,
    type,
    jobId: input.jobId,
    contractorId: input.contractorId,
    actorId: input.actorId,
    actorRole: input.actorRole,
    reason: sanitizedReason.trim(),
    createdAt: now,
    outcome,
    enforcementModeAtWrite: getNetworkEnforcementMode(),
  };
  return { events: [...input.events, event], event };
}

export function addDisputeEvidence(input: AddEvidenceInput): DisputeWriteResult {
  return appendNonOpen(input, "evidence_added");
}

export function withdrawDispute(input: WithdrawDisputeInput): DisputeWriteResult {
  return appendNonOpen(input, "withdraw", "withdrawn");
}

export function resolveDispute(input: ResolveDisputeInput): DisputeWriteResult {
  return appendNonOpen(input, "resolve", input.outcome);
}

// ── Derived state ─────────────────────────────────────────────────

export type DisputeStatus = "open" | "withdrawn" | "resolved";

export interface DisputeState {
  disputeId: string;
  status: DisputeStatus;
  outcome?: DisputeOutcome;
  jobId: string;
  contractorId: string;
  callbackId?: string;
  holdbackId?: string;
  openedAt: string;
  closedAt?: string;
  events: DisputeEvent[];
}

export function deriveDisputeState(
  events: DisputeEvent[],
  disputeId: string,
): DisputeState | null {
  const own = events
    .filter((e) => e.disputeId === disputeId)
    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
  if (own.length === 0) return null;
  const open = own.find((e) => e.type === "open");
  if (!open) return null;
  const terminal = own.find((e) => e.type === "withdraw" || e.type === "resolve");
  return {
    disputeId,
    status: terminal ? (terminal.type === "withdraw" ? "withdrawn" : "resolved") : "open",
    outcome: terminal?.outcome,
    jobId: open.jobId,
    contractorId: open.contractorId,
    callbackId: open.callbackId,
    holdbackId: open.holdbackId,
    openedAt: open.createdAt,
    closedAt: terminal?.createdAt,
    events: own,
  };
}

export function deriveAllDisputes(events: DisputeEvent[]): DisputeState[] {
  const ids = Array.from(new Set(events.map((e) => e.disputeId)));
  return ids
    .map((id) => deriveDisputeState(events, id))
    .filter((s): s is DisputeState => s !== null);
}

export function getOpenDisputesForContractor(
  events: DisputeEvent[],
  contractorId: string,
): DisputeState[] {
  return deriveAllDisputes(events).filter(
    (d) => d.contractorId === contractorId && d.status === "open",
  );
}

export function getDisputeBehavior() {
  return getEnforcementBehavior();
}
